import { Injectable, inject } from '@angular/core';
import { getAuth } from '@angular/fire/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import {
  Functions,
  getFunctions,
  httpsCallable,
} from '@angular/fire/functions';
import { NGXLogger } from 'ngx-logger';
import { Observable, map, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { AppExceptionModel } from '../models/app-exception.model';
import { Group, GroupType } from '../models/group.model';
import { Member } from '../models/member.model';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root',
})
export class GroupService {
  private afs: AngularFirestore = inject(AngularFirestore);
  private functions = inject(Functions);
  private logger = inject(NGXLogger);

  constructor() {}

  // Fetch user's groups from firestore
  getMyGroups(userId: string): Observable<Group[]> {
    let groupsList: Group[] = [];
    try {
      const docRef = this.afs.collection<Member>('memberships', (ref) =>
        ref.where('memberId', '==', userId),
      );

      const memberships$ = docRef.get().pipe(
        map((snapshot) => {
          if (snapshot.empty) return [];
          return snapshot.docs.map((doc) => {
            const member = doc.data() as Member;
            member.id = doc.id;
            return member;
          });
        }),
      );
      // console.log(
      //   'getMyGroups :: members: ',
      //   memberships$.forEach((m) => console.log(JSON.stringify(m)))
      // );

      memberships$.pipe(
        map((members) => {
          if (members.length == 0) return [];
          return members.map((member: Member) => {
            this.afs
              .collection('groups')
              .doc(member.groupId)
              .get()
              .pipe(
                map((groupSnapshot) => {
                  const group = groupSnapshot.data() as Group;
                  if (group && !group.groupType) {
                    group.groupType = GroupType.church;
                  }
                  group.memberType = member.memberType;
                  group.memberId = member.id;
                  group.memberStatus = member.status;
                  group.memberJoinDate = member.created;
                  group.memberColor =
                    member.memberColor ?? UtilsService.getRandomColor();
                  groupsList.push(group);
                }),
              );
          });
        }),
      );
      return of(groupsList);
    } catch (error) {
      // console.error('Error fetching groups', error);
    }
    return of(groupsList);
  }

  async createGroup(request: Group, username: string): Promise<any> {
    // createGroup is an onCall function: call it through the injected
    // Functions instance so the emulator setting is respected.
    if (!getAuth().currentUser) {
      this.logger.error('GroupService :: createGroup: user not authenticated');
      throw new Error('User not authenticated');
    }
    this.logger.debug(`GroupService :: createGroup: ${request?.name}`);
    try {
      const callable = httpsCallable(this.functions, 'createGroup');
      const result = await callable(request);
      this.logger.debug('GroupService :: createGroup: done', result.data);
      return result.data;
    } catch (error) {
      this.logger.error('GroupService :: createGroup: error', error);
      throw new Error('Error while creating group');
    }
  }

  async updateGroup(id: string, group: any): Promise<void> {
    // console.log(`updateGroup :: id: ${id}, group: ${JSON.stringify(group)}`);
    try {
      return await this.afs.collection('groups').doc(id).update(group);
    } catch (error) {
      // console.log(`updateGroup :: Error: ${error}`);
      return Promise.reject(error);
    }
  }

  async createBillingAccount(request: any): Promise<any> {
    try {
      const functions = getFunctions();
      const callFn = httpsCallable(functions, 'school_create_billingAccount');
      const result = await callFn(request);
      return result.data;
    } catch (error: any) {
      throw {
        code: 'ERROR_BILLING_ACCOUNT_CREATE',
        title: error.code ?? 'Billing Account Error',
        message: error.message ?? 'Failed to create billing account',
      } as AppExceptionModel;
    }
  }

  async setupSubscription(request: any): Promise<any> {
    try {
      const functions = getFunctions();
      const addMessage = httpsCallable(functions, 'school_create_subscription');
      const result = await addMessage(request);
      return result.data;
    } catch (error: any) {
      throw {
        code: 'ERROR_PYMT_SUB_CREATE',
        title: error.code ?? 'Billing Account Error',
        message: error.message ?? 'Failed to create billing account',
      } as AppExceptionModel;
    }
  }

  listenToGroupMetadata(groupId: string): Observable<any> {
    return this.afs
      .collection(environment.FIRESTORE_GROUPS_METADATA_COLLECTION)
      .doc(groupId)
      .valueChanges();
  }

  listenToGroupTokens(groupId: string): Observable<any> {
    return this.afs
      .collection('group_tokens')
      .doc(groupId)
      .valueChanges();
  }
}
